import { STORE_KEYS } from "../constants/StoreKeys.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import { sendCustomMessageOnSentry } from "../../sentryIntegration.jsx";
import { showConfirmationDialog } from "./utils.jsx";
import { SOCKET_CHANGES_UPDATE_NOTIFICATION } from "../constants/OtherKeys.jsx";
import { toast } from "sonner";

export const applyProjectChangesFromWS = ({
  stores, project, viewManager, userId, boardManager
}) => {
  const projectChanges = {};
  for (let storeName in stores) {
    projectChanges[storeName] = {};
    const store = project.getCrudStore(storeName);
    // Todo: fix needed, when user has multiple instances of same board opened in same machine then default view changes not reflected
    if (storeName === STORE_KEYS.viewsStore) {
      projectChanges[storeName] = store.swivuGetApplySocketChanges(stores[storeName]);
    } else if (storeName === STORE_KEYS.notifyPreferenceStore && stores[storeName].rows?.length && stores[storeName].rows[0].user_id !== userId) {
      continue;
    } else if  (storeName === STORE_KEYS.boardStore && stores[storeName].rows?.length) {
      const updatedBoardRecord = stores[storeName].rows[0];
      const existingBoardRecord = boardManager.boardNameStore.records.find((board) => board.id === updatedBoardRecord.id);
      if (existingBoardRecord) {
        projectChanges[storeName] = {
          ...projectChanges[storeName],
          updated: [updatedBoardRecord]
        }
        // our custom boardNameStore is mandatory to update than project.boardStore as project boardStore is there for reading the lastUpdatedBy text only
        boardManager.boardNameStore.applyChangeset({
          updated: [updatedBoardRecord]
        });
      }
    } else if (storeName !== "userpreferences") {
      if ("removed" in stores[storeName]) {
        projectChanges[storeName] = {
          ...projectChanges[storeName],
          removed: stores[storeName].removed
        }
      }
      if ("rows" in stores[storeName]) {
        stores[storeName].rows.forEach((record) => {
          if (!store.getById(record.id)) {
            if ("added" in projectChanges[storeName]) projectChanges[storeName].added.push(record);
            else {
              projectChanges[storeName] = {
                ...projectChanges[storeName],
                added: [record]
              }
            }
          } else {
            if (storeName === "events") record.duration = DateHelper.diff(new Date(record.start_time), new Date(record.end_time), "hour")
            if ("updated" in projectChanges[storeName]) projectChanges[storeName].updated.push(record);
            else {
              projectChanges[storeName] = {
                ...projectChanges[storeName],
                updated: [record]
              }
            }
          }
        })
      }
    }
  }
  project.applyProjectChanges(projectChanges);
  // handling current selected view for user
  // Hypothesis: viewManager could be undefined for certain time if the bryntum project is not loaded yet and websocket receives changes since the websocket connection gets established before project gets loaded
  if (STORE_KEYS.viewsStore in projectChanges && viewManager?.renderState) {
    const viewStoreChanges = projectChanges[STORE_KEYS.viewsStore];
    const updatedView = viewStoreChanges.updated?.[0];
    const selectedView = viewManager.renderState.selectedView;
    // if changes in currently selected viewstate then simply re-select the current view
    // selectedView.isDefaultView check is necessary as if the same board and account of user is opened in two different browsers
    // and one user attempts to update schedule timeaxis scroll the other user receives and try to make another update call cause
    // the ui to flicker indefinitely as both users are making infinite view update call (see task => https://admin.dev.swivu.com/swivu-engineering-rjv68i/scheduler?task=83538&tab=general)
    if (updatedView && updatedView.id === selectedView.id && !selectedView.isDefaultView) {
      const viewToSelect = viewManager.getViewById(updatedView.id);
      viewManager.selectView(viewToSelect);
    }
  }

  /* Notify stores that socket changes are applied */
  Object.keys(projectChanges).forEach(storeName => {
    const store = project.getCrudStore(storeName);
    store.trigger(SOCKET_CHANGES_UPDATE_NOTIFICATION)
  })
}

export const isOnlyUserPreferencesCall = (responseBody) => {
  const objectKeys = Object.keys(responseBody).filter((key) => typeof responseBody[key] === "object");
  return objectKeys.length === 1 && objectKeys.includes("userpreferences");
}

export const logSyncErrorToSentry = (e, requestData) => {
  const dataKeys = Object.keys(e.response || {}).join(",");
  sendCustomMessageOnSentry({
    messageTitle: `Project ${(e.requestType || "").toUpperCase()} Failed | (${dataKeys})`,
    logType: "error",
    extraDataBody: {
      requestData,
      responseData: e.response,
      responseOptions: e.responseOptions,
    },
  });
}

export const logErrorToSentry = (messageTitle, extraDataBody = {}) => {
  sendCustomMessageOnSentry({
    messageTitle,
    logType: "error",
    extraDataBody,
  });
}

export const showRequestFailErrorDialog = (e, jsonResponseKeys, newProject, excludeUserPrefChanges, jsonResponseText) => {
  if (jsonResponseKeys.includes("ai_error")) {
    toast.error("Failed to update setting", {
      description: jsonResponseText.ai_error || "Please try again or contact support if the issue persists."
    });
    newProject.revertChanges();
  } else if (jsonResponseText?.error?.includes("Invalid API Key")) {
    toast.error("OpenAI API Key Error", {
      description: "Invalid OpenAI API key provided. Please check your key and try again."
    });
    newProject.revertChanges();
  } else {
    showConfirmationDialog({
      title: "Something went wrong while writing your data to the server.",
      message: generateErrorMessage(e, jsonResponseKeys, excludeUserPrefChanges),
      confirmBtnText: { cls: 'order-2 b-raised', color: "b-blue", text: "Notify Swivu" },
      cancelBtnText: { cls: 'order-1', text: "Cancel" },
    }).then(() => {
      newProject.revertChanges();
    });
  }
}

const generateErrorMessage = (e, jsonResponseKeys, excludeUserPrefChanges) => {
  return `
    <b>Tip:</b>
    <p>If the problem occurs again, check your internet connection and reload the page.</p>
    ${jsonResponseKeys.length ? `
      <br/>
      <b>The following change(s) did not write to the server:</b>
      <ul class="my-[5px]">
        ${jsonResponseKeys.map((key) => `<li>${key}</li>`).join("")}
      </ul>
    ` : ''}
    <br/>
    <details open>
      <summary>Technical Details</summary>
      <p class="ml-[1em]"><b>Timestamp</b> ${DateHelper.format(new Date(), "M/D/YYYY hh:mm A")}</p>
      <p><b>Response data:</b></p>
      <p class="ml-[1em]">${e.responseText}</p>
      <p><b>Request data:</b></p>
      <p class="ml-[1em]"><code>${JSON.stringify(excludeUserPrefChanges)}</code></p>
    </details>
  `;
}
